import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface AnalyticsEvent {
  id?: string;
  user_id: string;
  event_type: string;
  event_data: Record<string, any>;
  session_id?: string;
  created_at?: string;
}

export interface DashboardStats {
  user_id: string;
  total_bills: number;
  total_amount: number;
  avg_confidence: number;
  completed_bills: number;
  error_bills: number;
  bills_last_30_days: number;
  bills_last_7_days: number;
}

export const useAnalytics = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track analytics event
  const trackEvent = useMutation({
    mutationFn: async (event: Omit<AnalyticsEvent, 'id' | 'user_id' | 'created_at'>) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('analytics_events')
        .insert({
          user_id: user.id,
          event_type: event.event_type,
          event_data: event.event_data,
          session_id: event.session_id || 'default-session'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onError: (error: any) => {
      console.error('Failed to track analytics event:', error);
      toast({
        title: "Analytics Error",
        description: "Failed to track user interaction.",
        variant: "destructive",
      });
    }
  });

  // Get analytics events
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ['analytics-events', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('analytics_events')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data as AnalyticsEvent[];
    },
    enabled: !!user?.id,
  });

  // Get optimized dashboard stats
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats-optimized', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase.rpc('get_dashboard_stats');

      if (error) throw error;
      return data?.[0] as DashboardStats || null;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Get system metrics
  const { data: systemMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['system-metrics'],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('system_metrics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  return {
    trackEvent,
    events,
    eventsLoading,
    dashboardStats,
    statsLoading,
    systemMetrics,
    metricsLoading,
  };
};

// Convenience functions for common events
export const useAnalyticsTracking = () => {
  const { trackEvent } = useAnalytics();

  const trackPageView = (page: string) => {
    trackEvent.mutate({
      event_type: 'page_view',
      event_data: { page, timestamp: Date.now() }
    });
  };

  const trackUserAction = (action: string, details: Record<string, any> = {}) => {
    trackEvent.mutate({
      event_type: 'user_action',
      event_data: { action, ...details, timestamp: Date.now() }
    });
  };

  const trackError = (error: string, context: Record<string, any> = {}) => {
    trackEvent.mutate({
      event_type: 'error',
      event_data: { error, ...context, timestamp: Date.now() }
    });
  };

  return {
    trackPageView,
    trackUserAction,
    trackError,
  };
};